import logo from "@/assets/logo.png";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    CALENDAR_ENTRY,
    EXTERNAL_REGISTRATIONS_ENTRY,
    HOME_ENTRY,
    KEY_OBJECTS,
    LAYOUT_AUDIENCES,
    MEDREP_ENTRY,
    MenuEntry,
    OTHER_OBJECTS,
    SALES_FORMS_ENTRY,
    useMenuEntryAccess,
    useObjectMenuPermissions
} from "@/lib/navigation";
import { cn, stringToColor } from "@/lib/utils";
import { useAuth } from "@/providers/auth";
import { useUser } from "@/providers/user";
import { AvatarFallback } from "@radix-ui/react-avatar";
import {
    Baby,
    BarChart3,
    ChartNoAxesCombined,
    CheckCheck,
    ChevronDown,
    Cross,
    ExternalLink,
    FileUser,
    GraduationCap,
    KeyRound,
    LayoutList,
    LibraryBig,
    LogOut,
    Mailbox,
    MapPinned,
    Merge,
    Palette,
    Pen,
    PhoneCall,
    PhoneOff,
    Puzzle,
    ReceiptEuro,
    Recycle,
    Search,
    Settings,
    ShoppingCart,
    Zap
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Favorites } from "./favorites";
import { FavoriteDropdown } from "./favorites/dropdown";
import { NotificationPopover } from "./notification/popover";
import { RecentRecords } from "./recent-records";
import { SearchBox } from "./search/search-box";
import { Avatar } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ViewAsBanner } from "./view-as-banner";

import { GlobalActionsDropdown } from "./global-actions/global-actions-dropdown";
import { SimpleTooltip } from "./ui/simple-tooltip";

interface MainMenuProps extends React.HTMLAttributes<HTMLDivElement> {}

function SimpleMenuItem({ text, href, isScrolled }: { text: string; href: string; isScrolled?: boolean }) {
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    const isClicked = location.pathname === href || location.pathname.startsWith(href + "/");

    return (
        <NavLink to={href} className="" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <div className="group relative  flex items-center ">
                {isClicked && !isScrolled && (
                    <div className="absolute -left-2 bottom-0 z-50 h-2 w-2 overflow-hidden bg-gray-100 transition-all duration-300 ease-in-out">
                        <div className="absolute -left-2 -top-2 h-4 w-4  rounded-full bg-white shadow-inner" />
                    </div>
                )}
                <div
                    className={cn(
                        "relative flex transform items-center justify-center rounded-t-lg px-2 pb-1 transition-all duration-300 ease-in-out",
                        isScrolled ? "mb-2 mt-1 h-8 rounded-b-lg pb-0" : "h-10",
                        isClicked
                            ? isScrolled
                                ? "translate-y-0 bg-gradient-to-t from-gray-200/60 to-gray-200/80 underline-offset-4 shadow-sm hover:underline"
                                : "translate-y-0 bg-gradient-to-t from-gray-100 to-gray-200/80 underline-offset-4 shadow-md hover:underline"
                            : "translate-y-1"
                    )}
                >
                    <div className={cn("flex h-6 items-center justify-center whitespace-nowrap rounded-lg px-1 font-medium text-gray-500", "text-sm", (isHovered || isClicked) && " text-black")}>
                        {text}
                    </div>
                </div>
                {isClicked && !isScrolled && (
                    <div className="absolute -right-2 bottom-0 z-50 h-2 w-2 overflow-hidden bg-gray-100 transition-all duration-300 ease-in-out">
                        <div className="absolute -right-2 -top-2 h-4 w-4  rounded-full  bg-white shadow-inner" />
                    </div>
                )}
            </div>
        </NavLink>
    );
}
function ObjectMenuItem({ text, href, isScrolled, quickAccess = true }: { text: string; href: string; isScrolled?: boolean; quickAccess?: boolean }) {
    const [isHovered, setIsHovered] = useState(false);
    const location = useLocation();
    const isClicked = location.pathname === href || location.pathname.startsWith(href + "/");
    const [open, setOpen] = useState(false);
    const [alignOffset, setAlignOffset] = useState(-50);
    const divRef = useRef<HTMLDivElement>(null);

    // Extrait le type d'objet depuis l'URL (ex: "/opportunities" -> "opportunity")
    const getObjectTypeFromHref = (href: string): string | null => {
        for (const key in localStorage) {
            if (key.startsWith("options-metadata:")) {
                const cached = localStorage.getItem(key);
                if (cached) {
                    try {
                        const { data } = JSON.parse(cached);
                        if (data?.object_url && href.startsWith(`/${data.object_url}`)) {
                            return data.object_type;
                        }
                    } catch {
                        continue;
                    }
                }
            }
        }
        return null;
    };

    const objectType = getObjectTypeFromHref(href) || "";

    const handlePopoverTriggerClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Mesure la largeur du div avant d'ouvrir le popover
        if (divRef.current) {
            const width = divRef.current.offsetWidth;
            // Ajuste l'alignOffset en fonction de la largeur
            // Plus le div est large, plus on décale vers la gauche
            setAlignOffset(-width + 24);
        }

        setOpen(!open);
    };

    return (
        <div className="relative">
            <NavLink to={href} className="" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                <div className="group relative  flex items-center ">
                    {isClicked && !isScrolled && (
                        <div className="absolute -left-2 bottom-0 z-50 h-2 w-2 overflow-hidden bg-gray-100 transition-all duration-300 ease-in-out">
                            <div className="absolute -left-2 -top-2 h-4 w-4  rounded-full bg-white shadow-inner" />
                        </div>
                    )}
                    <div
                        ref={divRef}
                        className={cn(
                            "relative flex transform items-center justify-center rounded-t-lg px-2 pb-1 transition-all duration-300 ease-in-out",
                            isScrolled ? "mb-2 mt-1 h-8 rounded-b-lg pb-0" : "h-10",
                            isClicked
                                ? isScrolled
                                    ? "translate-y-0 bg-gradient-to-t from-gray-200/60 to-gray-200/80 underline-offset-4 shadow-sm hover:underline"
                                    : "translate-y-0 bg-gradient-to-t from-gray-100 to-gray-200/80 underline-offset-4 shadow-md hover:underline"
                                : "translate-y-1"
                        )}
                    >
                        <div className={cn("flex h-6 items-center justify-center whitespace-nowrap rounded-lg px-1 font-medium text-gray-500", "text-sm", (isHovered || isClicked) && " text-black")}>
                            {text}
                        </div>
                        {quickAccess && (
                        <Popover open={open} onOpenChange={setOpen}>
                            <PopoverTrigger asChild>
                                <ChevronDown size={16} className={cn(" text-gray-500", (isHovered || isClicked) && " text-black")} onClick={handlePopoverTriggerClick} />
                            </PopoverTrigger>
                            <PopoverContent className="w-72 max-w-72 overflow-hidden p-0" align="start" alignOffset={alignOffset} sideOffset={10}>
                                <div className="w-72 max-w-72 overflow-hidden">
                                    <ScrollArea className="h-72 w-full">
                                        <div className="space-y-4 px-2 py-2">
                                            <div className="space-y-1">
                                                <div className="truncate px-1 py-1 text-sm font-medium">Favorites</div>

                                                <Favorites objectType={objectType} />
                                            </div>
                                            <div className="space-y-1">
                                                <div className="truncate px-1 py-1 text-sm font-medium">Recent records</div>

                                                <RecentRecords objectType={objectType} />
                                            </div>
                                        </div>
                                    </ScrollArea>
                                </div>
                            </PopoverContent>
                        </Popover>
                        )}
                    </div>
                    {isClicked && !isScrolled && (
                        <div className="absolute -right-2 bottom-0 z-50 h-2 w-2 overflow-hidden bg-gray-100 transition-all duration-300 ease-in-out">
                            <div className="absolute -right-2 -top-2 h-4 w-4  rounded-full  bg-white shadow-inner" />
                        </div>
                    )}
                </div>
            </NavLink>
        </div>
    );
}

function ExpandedMenuItem({
    text,
    items,
    subMenus
    // isScrolled
}: {
    text: string;
    items: MenuEntry[];
    subMenus?: { title: string; items: MenuEntry[] }[];
    isScrolled?: boolean;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout>();

    const handleMouseEnter = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, 100);
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen} modal={false}>
            <DropdownMenuTrigger asChild>
                <div
                    className={cn("flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-lg px-2 pt-1 font-medium text-gray-500 transition-colors hover:text-black", "h-6 text-sm")}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {text}
                    <ChevronDown className={cn("h-4 w-4")} />
                </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" sideOffset={6} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                {subMenus?.map(subMenu => (
                    <DropdownMenuSub key={subMenu.title}>
                        <DropdownMenuSubTrigger>{subMenu.title}</DropdownMenuSubTrigger>
                        <DropdownMenuPortal>
                            <DropdownMenuSubContent className="z-50 rounded-md border bg-white p-1 shadow-md" sideOffset={8} alignOffset={-5}>
                                {subMenu.items.map(component => (
                                    <DropdownMenuItem key={component.title} asChild>
                                        <NavLink to={component.href} className="flex cursor-pointer items-center gap-2">
                                            {component.icon}
                                            {component.title}
                                        </NavLink>
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuSubContent>
                        </DropdownMenuPortal>
                    </DropdownMenuSub>
                ))}
                {items.map(component => (
                    <DropdownMenuItem key={component.title} asChild>
                        <NavLink to={component.href} className="flex cursor-pointer items-center gap-2">
                            {component.icon}
                            {component.title}
                        </NavLink>
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export function MainMenu2({}: MainMenuProps) {
    const { user, has_any_group, has_any_profile, is_visible_to_audience } = useUser();
    const { signout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Every entry declares its audiences (default: standard only), see `@/lib/navigation`.
    const canSee = useMenuEntryAccess();
    const isIperiaOperator = user?.profile === "iperia_operator";
    const apprenticeship_teams =
        is_visible_to_audience() && has_any_group(["app_apprenticeship_admin", "team_apprenticeship_cap", "team_apprenticeship_ce", "team_apprenticeship_csm"]);
    // Top-level "Registrations" is only for true external providers.
    // Internal users (system_admin, registration/finance) keep Tools > Registrations.
    const show_external_registrations_menu =
        canSee(EXTERNAL_REGISTRATIONS_ENTRY) &&
        (!!user?.external_provider || (user?.external_providers?.length ?? 0) > 0) &&
        !has_any_profile(["system_admin"]) &&
        !has_any_group(["app_registration_admin", "team_finance"]);

    // État pour gérer l'apparence lors du scroll
    const [isScrolled, setIsScrolled] = useState(false);

    // États pour gérer l'ouverture des dropdowns au hover
    const [avatarOpen, setAvatarOpen] = useState(false);

    // Hook pour détecter le scroll avec throttling pour des transitions plus fluides
    useEffect(() => {
        let ticking = false;

        const handleScroll = () => {
            if (!ticking) {
                requestAnimationFrame(() => {
                    const scrollY = window.scrollY;
                    setIsScrolled(scrollY > 20);
                    ticking = false;
                });
                ticking = true;
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const catalogs: MenuEntry[] = [
        {
            title: "Health",
            href: "/catalogue/sante",
            icon: <Cross size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />
        },
        {
            title: "Generalist",
            href: "/catalogue/generaliste",
            icon: <LibraryBig size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />
        },
        {
            title: "Alternance",
            href: "/catalogue/alternance",
            icon: <GraduationCap size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />
        },
        {
            title: "Edumiam",
            href: "/catalogue/edumiam",
            icon: <Baby size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />
        },
    ];
    const apprenticeships: MenuEntry[] = [
        {
            title: "Candidates",
            href: "/alternance/candidats",

            icon: (
                <p>
                    <FileUser size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />
                </p>
            ),
            groups: ["app_apprenticeship_admin", "team_apprenticeship_ce", "team_apprenticeship_sales", "team_apprenticeship_cap", "team_apprenticeship_csm", "team_support"]
        },
        {
            title: "The Map",
            href: "/alternance/carte",

            icon: (
                <p>
                    <MapPinned size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />
                </p>
            ),
            groups: ["app_apprenticeship_admin", "team_apprenticeship_ce", "team_apprenticeship_sales", "team_apprenticeship_cap", "team_apprenticeship_csm", "team_support"]
        },
        {
            title: "My Matches",
            href: "/alternance/matchs",

            icon: (
                <p>
                    <Puzzle size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />
                </p>
            ),
            groups: ["app_apprenticeship_admin", "team_apprenticeship_ce", "team_apprenticeship_sales", "team_apprenticeship_cap", "team_apprenticeship_csm", "team_support"]
        },
        {
            title: "Students",
            href: "/alternance/etudiants",
            icon: (
                <p>
                    <GraduationCap size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />
                </p>
            ),
            groups: ["app_apprenticeship_admin", "team_apprenticeship_csm", "team_support"]
        }
    ];

    const registrations: MenuEntry[] = [
        {
            title: "Sale Validation",
            href: "/outils/ventes",
            icon: <CheckCheck size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin"]
        },
        {
            title: "Autonomous Registrations",
            href: "/inscriptions/autonomes",
            icon: <ShoppingCart size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_registration_admin"]
        },
        {
            title: "External Registrations",
            href: "/inscriptions/externes",
            icon: <ExternalLink size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_registration_admin", "team_finance", "team_external_provider"]
        }
    ];

    const billing: MenuEntry[] = [
        {
            title: "ANDPC Billing",
            href: "/facturation/andpc/",
            icon: <ReceiptEuro size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_billing_admin", "team_finance", "team_support"]
        },
        {
            title: "FIFPL Billing",
            href: "/facturation/fifpl/",
            icon: <ReceiptEuro size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_billing_admin", "team_finance", "team_support"]
        },
        {
            title: "Invoice Correction",
            href: "/outils/correction-facture/",
            icon: <Pen size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin", "team_support", "team_finance"]
        }
    ];

    const tools: MenuEntry[] = [
        {
            title: "Merge",
            href: "/outils/fusion",
            icon: <Merge size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin", "team_support", "team_csm", "team_adv"]
        },
        {
            title: "Bloctel",
            href: "/outils/bloctel",
            icon: <PhoneOff size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: ["app_tools_admin", "team_support", "team_csm", "team_adv"]
        },

        {
            icon: <Search size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            title: "SIRET Search",
            href: "/outils/siret",
            groups: ["app_tools_admin"]
        },
        {
            icon: <Zap size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            title: "New Flash Injection",
            href: "/outils/injection-flash/new",
            groups: ["app_tools_admin"]
        },
        {
            icon: <Mailbox size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            title: "Mailing Campaigns",
            href: "/outils/campagnes-postales",
            groups: ["app_tools_admin", "app_tools_mailing_campaign"]
        },
        {
            icon: <PhoneCall size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            title: "Diabolocom campaigns",
            href: "/diabolocom-campaigns",
            groups: ["app_tools_admin"]
        },
        {
            icon: <BarChart3 size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            title: "Reports",
            href: "/outils/rapports",
            groups: []
        },
        {
            icon: <KeyRound size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            title: "Coffre",
            href: "/outils/coffre",
            groups: []
        },
        {
            title: "Lead recycling",
            href: "/outils/recyclage-leads",
            icon: <Recycle size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: [],
            profiles: ["sales_admin", "system_admin"]
        },
        {
            title: "Design system",
            href: "/outils/design-system",
            icon: <Palette size={16} className="mb-0.5 mr-1.5 inline-block" strokeWidth={2} />,
            groups: []
        }
    ];

    const { isObjectEntryVisible, isLoadingPermissions } = useObjectMenuPermissions();
    const key_objects = KEY_OBJECTS.filter(isObjectEntryVisible);
    const other_objects = OTHER_OBJECTS.filter(isObjectEntryVisible);
    const visible_catalogs = catalogs.filter(canSee);
    const visible_apprenticeships = apprenticeships.filter(canSee);
    const visible_tools = tools.filter(canSee);
    const tool_sub_menus = [
        { title: "Registrations", items: registrations.filter(canSee) },
        { title: "Billing", items: billing.filter(canSee) }
    ].filter(subMenu => subMenu.items.length > 0);

    return (
        <div className="fixed top-0 z-50 w-full">
            <ViewAsBanner />
            <div className={cn("flex flex-col justify-between gap-y-1.5 bg-white px-4 pt-2 transition-all duration-300 ease-in-out", isScrolled ? "rounded-b-2xl shadow-md" : "")}>
                <div className="flex w-full items-center justify-between">
                    <NavLink
                        to="/"
                        className={cn(
                            "group inline-flex w-max items-center justify-center px-4 py-2 text-sm font-medium transition-all duration-300 ease-in-out disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-muted/50",
                            isScrolled ? "h-8" : "h-10"
                        )}
                    >
                        <img src={logo} alt="Walter CRM" className={cn("min-w-[118px] transition-all duration-300 ease-in-out", "h-[18px]")} />
                    </NavLink>

                    <SearchBox placeholder="Search" />

                    <div className="flex items-center justify-between ">
                        <div className="mr-2 flex items-center gap-2">
                            {!isIperiaOperator && is_visible_to_audience(LAYOUT_AUDIENCES.perfTracking) && (
                                <div className={cn("relative flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100")} onClick={() => navigate("/objectif/mensuel")}>
                                    <SimpleTooltip message="Perf. tracking" delayDuration={300} skipDelayDuration={300}>
                                        <div className="flex h-8 items-center justify-center rounded-full  text-slate-500">
                                            <ChartNoAxesCombined className="text-gray-700" size={19} strokeWidth={1.5} />
                                        </div>
                                    </SimpleTooltip>
                                </div>
                            )}

                            {is_visible_to_audience(LAYOUT_AUDIENCES.notifications) && <NotificationPopover />}
                            {!isIperiaOperator && is_visible_to_audience(LAYOUT_AUDIENCES.globalActions) && <GlobalActionsDropdown />}
                            {is_visible_to_audience(LAYOUT_AUDIENCES.quickAccess) && <FavoriteDropdown />}
                        </div>

                        <DropdownMenu open={avatarOpen} onOpenChange={setAvatarOpen}>
                            <DropdownMenuTrigger asChild>
                                <Avatar className={cn("ml-1 cursor-pointer transition-all duration-300 ease-in-out", "h-8 w-8")}>
                                    <AvatarFallback
                                        className={cn("!m-0 flex items-center justify-center !p-0 text-center text-white transition-all duration-300 ease-in-out", "h-8 w-8 text-sm")}
                                        style={{ backgroundColor: stringToColor((user?.first_name || "") + (user?.last_name || "")) }}
                                    >
                                        {user?.first_name?.[0]}
                                        {user?.last_name?.[0] || ".."}
                                    </AvatarFallback>
                                </Avatar>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 ">
                                <div className="mb-0.5 flex items-center justify-start gap-2 p-2">
                                    <div className="flex flex-col space-y-1">
                                        <p className="text-base font-medium leading-none">
                                            {user?.first_name} {user?.last_name}
                                        </p>
                                        <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                                    </div>
                                </div>
                                {is_visible_to_audience(LAYOUT_AUDIENCES.profileLinks) && (
                                    <DropdownMenuItem asChild>
                                        <NavLink to={`/users/${user?.sf_user?.id}`} className="flex cursor-pointer items-center gap-2">
                                            <FileUser size={16} />
                                            My profile
                                        </NavLink>
                                    </DropdownMenuItem>
                                )}
                                {apprenticeship_teams && (
                                    <DropdownMenuItem asChild>
                                        <NavLink to="/activites" className="flex cursor-pointer items-center gap-2">
                                            <LayoutList size={16} />
                                            My activities
                                        </NavLink>
                                    </DropdownMenuItem>
                                )}
                                {is_visible_to_audience(LAYOUT_AUDIENCES.profileLinks) && (
                                    <DropdownMenuItem asChild>
                                        <NavLink to="/parametres" className="flex cursor-pointer items-center gap-2">
                                            <Settings size={16} />
                                            Settings
                                        </NavLink>
                                    </DropdownMenuItem>
                                )}
                                <DropdownMenuItem
                                    onClick={async () => {
                                        await signout();
                                        navigate("/connexion", { replace: true });
                                    }}
                                    className="flex cursor-pointer items-center gap-2"
                                >
                                    <LogOut size={16} />
                                    Logout
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>

                <div className={cn("flex w-full items-center gap-1 transition-all duration-300 ease-in-out", "pb-0")}>
                    <div className="scrollbar-hide flex items-center overflow-x-auto overflow-y-hidden px-4">
                        {isIperiaOperator ? (
                            <SimpleMenuItem text="IPERIA" href="/inscriptions/iperia" isScrolled={isScrolled} />
                        ) : (
                            <>
                        {canSee(HOME_ENTRY) && <SimpleMenuItem text={HOME_ENTRY.title} href={HOME_ENTRY.href} isScrolled={isScrolled} />}

                        {show_external_registrations_menu && <SimpleMenuItem text={EXTERNAL_REGISTRATIONS_ENTRY.title} href={EXTERNAL_REGISTRATIONS_ENTRY.href} isScrolled={isScrolled} />}

                        {canSee(MEDREP_ENTRY) && !has_any_group(["app_registration_admin"]) && <SimpleMenuItem text="Contacts" href={MEDREP_ENTRY.href} isScrolled={isScrolled} />}

                        {/* <SimpleMenuItem text="Reports" href="/reports" isScrolled={isScrolled} /> */}
                        {canSee(SALES_FORMS_ENTRY) && user?.profile != "default" && <SimpleMenuItem text={SALES_FORMS_ENTRY.title} href={SALES_FORMS_ENTRY.href} isScrolled={isScrolled} />}

                        {canSee(CALENDAR_ENTRY) && <SimpleMenuItem text={CALENDAR_ENTRY.title} href={CALENDAR_ENTRY.href} isScrolled={isScrolled} />}
                        {visible_catalogs.length > 0 && <ExpandedMenuItem text="Catalogs" items={visible_catalogs} isScrolled={isScrolled} />}

                        {isLoadingPermissions ? (
                            <div className="translate-y-1 px-2 pb-1 text-sm font-medium text-gray-500">Loading...</div>
                        ) : (
                            key_objects.map(component => {
                                return (
                                    <ObjectMenuItem
                                        key={component.title}
                                        text={component.title}
                                        href={component.href}
                                        isScrolled={isScrolled}
                                        quickAccess={is_visible_to_audience(LAYOUT_AUDIENCES.quickAccess)}
                                    />
                                );
                            })
                        )}

                        {!isLoadingPermissions && other_objects.length > 0 && (
                            <ExpandedMenuItem text="Other Objects" items={[...other_objects].sort((a, b) => a.title.localeCompare(b.title))} isScrolled={isScrolled} />
                        )}

                        {visible_apprenticeships.length > 0 && <ExpandedMenuItem text="Apprenticeships" items={visible_apprenticeships} isScrolled={isScrolled} />}

                        {(visible_tools.length > 0 || tool_sub_menus.length > 0) && <ExpandedMenuItem text="Tools" items={visible_tools} isScrolled={isScrolled} subMenus={tool_sub_menus} />}

                        {canSee(MEDREP_ENTRY) && has_any_group(["app_registration_admin"]) && <SimpleMenuItem text="Medical Reps" href={MEDREP_ENTRY.href} isScrolled={isScrolled} />}

                        {/* The entry of the current page when it sits in a dropdown (as before: not checked against groups). */}
                        {OTHER_OBJECTS.concat(catalogs)
                            .concat(apprenticeships)
                            .concat(tools)
                            .concat(registrations)
                            .filter(component => is_visible_to_audience(component.audiences))
                            .map(component => {
                                const isActive = location.pathname.startsWith(component.href + "/") || location.pathname == component.href;
                                return isActive && OTHER_OBJECTS.includes(component) ? (
                                    <ObjectMenuItem
                                        key={component.title}
                                        text={component.title.length > 22 ? component.title.slice(0, 22) + "..." : component.title}
                                        href={component.href}
                                        isScrolled={isScrolled}
                                        quickAccess={is_visible_to_audience(LAYOUT_AUDIENCES.quickAccess)}
                                    />
                                ) : isActive && !OTHER_OBJECTS.includes(component) ? (
                                    <SimpleMenuItem
                                        key={component.title}
                                        text={component.title.length > 22 ? component.title.slice(0, 22) + "..." : component.title}
                                        href={component.href}
                                        isScrolled={isScrolled}
                                    />
                                ) : null;
                            })
                            .filter(Boolean)}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
